import { useEffect, useState } from "react";
import { chakra, Flex, Grid, GridItem, Heading, Spinner } from "@chakra-ui/react";
import { ComponentProps } from "@/baseComponents";
import { CustomGrid } from "@/components/layout/grid";
import { Card, CardProps } from "@/components/molecules/card";
import { FilterGroup, FilterOption, OverviewPageFilters } from "@/utils/filterOptionsContext";
import { Filters, useFilteredCardStore } from "@/stores/filteredCardStore";
import { useOverviewResultsStore } from "@/stores/overviewResultsStore";
import Pagination from "@/components/molecules/pagination/Pagination";
import type { PageInfo } from "@/components/molecules/pagination/Pagination";
import { Filter } from "@/components/molecules/filter";
import { useTranslations } from 'next-intl'
import { CardTypes, Cultures } from "@/types/enums";
import FundsOverviewGrid from "../fundsOverviewGrid/FundsOverviewGrid";

export interface OverviewBlockProps extends ComponentProps {
  pageSize: number
  gridColumns?: number
  filterOptions: FilterGroup[]
  showFilters?: OverviewPageFilters
  culture: Cultures
  contentQuery: (
    pageInfo: PageInfo,
    selectedFilters: Filters,
    setResultCallback: (result: OverviewContentQueryResult) => void,
  ) => void
}

export interface OverviewContentQueryResult {
  currentPageCards: CardProps[]
  totalItemCount: number
}

export default chakra(OverviewBlock)

export function OverviewBlock({
  pageSize,
  gridColumns,
  contentQuery,
  filterOptions,
  showFilters,
  culture,
  theme = 'brand',
}: OverviewBlockProps): JSX.Element {
  const t = useTranslations()
  const selectedFilters = useFilteredCardStore((state) => state.selectedFilters)

  const [isLoading, setIsLoading] = useState(false)
  const [pagination, setPagination] = useState<PageInfo>({
    page: 1,
    pageSize: pageSize,
  })

  const result = useOverviewResultsStore((state) => state.result)
  const hasNoResults =
    !result?.currentPageCards || result?.currentPageCards?.length === 0
  const updateOverviewResults = useOverviewResultsStore(
    (state) => state.updateOverviewResults,
  )
  const startItem = ((pagination?.page ?? 1) - 1) * pageSize + 1
  const endItem = Math.min(
    (pagination.page ?? 1) * pageSize,
    result?.totalItemCount ?? 0,
  )

  const isFundsOverview = result?.currentPageCards?.some(
    (card) => card.cardType === CardTypes.FundsAndMandates,
  );

  // State to track the index of the hovered card
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  useEffect(() => {
    setIsLoading(true)
    contentQuery &&
      contentQuery(pagination, selectedFilters, (newData) => {
        updateOverviewResults(newData)
        setIsLoading(false)
      })
  }, [pagination, selectedFilters, contentQuery, updateOverviewResults])

  const filters: FilterGroup[] = []

  const getFiltersForGroup = (key: string) => {
    const filtersForGroup = filterOptions?.filter(
      (group) => group.groupName === key,
    )[0]?.filters
    return filtersForGroup ?? []
  }

  if (showFilters?.articleTypes) {
    filters.push({
      groupName: 'articleType',
      groupDisplayName: t('articles'),
      filters: getFiltersForGroup('articleType'),
    })
  }
  if (showFilters?.portfolioTypes) {
    filters.push({
      groupName: 'portfolioType',
      groupDisplayName: t('portfolio'),
      filters: getFiltersForGroup('portfolioType'),
    })
  }
  if (showFilters?.strategies) {
    filters.push({
      groupName: 'strategy',
      groupDisplayName: t('strategies'),
      filters: getFiltersForGroup('strategy'),
    })
  }
  if (showFilters?.funds) {
    filters.push({
      groupName: 'fund',
      groupDisplayName: t('funds'),
      filters: getFiltersForGroup('fund'),
    })
  }
  if (showFilters?.years) {
    // Generate year filter options from current year down to 2019
    // Content in CMS goes back to 2019, so we start from there
    const currentYear = new Date().getFullYear();
    const filterOptions: FilterOption[] = [];

    for (let year = currentYear; year >= 2019; year--) {
      const yearStr = year.toString();
      filterOptions.push({
        displayName: yearStr,
        value: yearStr
      });
    }

    filters.push({
      groupName: 'year',
      groupDisplayName: t('year'),
      filters: filterOptions,
    });
  }

  return (
    <CustomGrid>
      {(result?.totalItemCount ?? 0) > 0 && (
        <GridItem colSpan={12} display='flex'>
          <Flex flexDirection='column' gap='1.25rem'>
            {t('results')} {startItem}
            {result?.totalItemCount !== startItem && ` - ${endItem}`} van{' '}
            {result?.totalItemCount}
          </Flex>
        </GridItem>
      )}
      {showFilters && (
        <GridItem
          colSpan={{ base: 12, md: 3, xl: 2 }}
          position={'relative'}
          minHeight='4.125rem'
        >
          <Filter
            width='100%'
            filters={filters}
            clearFiltersText={t('clearFilters')}
            resetPagination={() => {
              setPagination({
                page: 1,
                pageSize: pageSize,
              })
            }}
          />
        </GridItem>
      )}
      <GridItem colSpan={showFilters ? { base: 12, md: 9, xl: 10 } : 12}>
        {/* Loader */}
        {isLoading && (
          <Flex width='100%' justifyContent='center' py={48}>
            <Spinner color={`${theme}.primary`} size='xl' />
          </Flex>
        )}

        {/* No results */}
        {!isLoading && hasNoResults && (
          <Flex width='100%' justifyContent='center' py={48}>
            <Heading as='h3' fontSize='lg' color={`${theme}.tertiary`}>
              {t('noResults')}
            </Heading>
          </Flex>
        )}

        {/* Results */}
        {!isLoading && !hasNoResults && (
          <Grid
            templateColumns="repeat(12, 1fr)"
            columnGap={{
              md: 10,
              lg: isFundsOverview ? 20 : 10,
            }}
            rowGap={{base: isFundsOverview ? 8 : 10, md: isFundsOverview ? 20 : 10}}
          >
            {isFundsOverview ? (
              <FundsOverviewGrid cards={result?.currentPageCards ?? []} />
            ) : (
              result?.currentPageCards?.map((cardProps, index) => (
                <GridItem
                  key={cardProps.id}
                  colSpan={{ base: 12, md: gridColumns }}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  style={{
                    transition: 'opacity 0.3s ease',
                    opacity:
                      hoveredIndex === null || hoveredIndex === index ? 1 : 0.4,
                  }}
                >
                  <Card culture={culture} {...cardProps} />
                </GridItem>
              ))
            )}

            {/* Pagination */}
            {result?.totalItemCount && result.totalItemCount > pageSize && (
              <GridItem colSpan={12}>
                <Flex justifyContent='center' paddingTop={4}>
                  <Pagination
                    page={pagination.page}
                    pageSize={pagination.pageSize}
                    pageCount={result?.totalItemCount ?? 0}
                    onPageChange={setPagination}
                  />
                </Flex>
              </GridItem>
            )}
          </Grid>
        )}
      </GridItem>
    </CustomGrid>
  )
}
